import { useCallback, useMemo, useState } from 'react';
import { useDevice } from './useDevice';
import { usePendingMTTracking } from './usePendingMTTracking';
import { commandService } from '@/api/services';
import type { NAMTMessageType } from '@/types/enums';

/**
 * Consolidates the on-demand-command send pattern used throughout
 * Configuration (`ConfigurationPage.tsx`'s `sendCommand`) into one reusable
 * hook: send via `commandService.sendOnDemandCommand`, and on success
 * register it with `usePendingMTTracking` (so pending status is accurate
 * immediately and through the post-confirmation grace period) and trigger an
 * immediate refetch so the server list catches up quickly. Pair with
 * `useFieldPending` for the per-control optimistic display value.
 */
export function useMTCommand(deviceUUID: string) {
  const { selectedDeviceInfo, refreshDeviceInfo } = useDevice();
  const rawMtObjList = useMemo(() => selectedDeviceInfo?.mtObjList ?? [], [selectedDeviceInfo]);
  const { mergedMtObjList: mtObjList, registerSentMT } = usePendingMTTracking(rawMtObjList);
  const [feedback, setFeedback] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  const sendCommand = useCallback(
    async (cmd: NAMTMessageType, dataStrList?: string[]) => {
      try {
        const resp = await commandService.sendOnDemandCommand({
          deviceUUID,
          mtMessageType: cmd,
          ...(dataStrList ? { dataStrList } : {}),
        });
        if (resp.isSuccess) {
          setFeedback({ type: 'success', message: 'Command sent successfully. Changes will apply on next device check-in.' });
          registerSentMT(cmd, resp.mtmsn, dataStrList);
          refreshDeviceInfo();
        } else {
          setFeedback({ type: 'error', message: 'Command was not accepted by the server.' });
        }
        return resp.isSuccess;
      } catch {
        setFeedback({ type: 'error', message: 'Failed to send command.' });
        return false;
      }
    },
    [deviceUUID, refreshDeviceInfo, registerSentMT],
  );

  return { sendCommand, mtObjList, feedback, setFeedback };
}
